import React from "react";
import { Link } from "react-router-dom";
import ComingSoonIllustration from "./ComingSoonIllustration";

/**
 * Reusable Coming Soon content block for planned but unavailable features.
 *
 * @param {object} props
 * @param {string} [props.title]
 * @param {string} [props.description]
 * @param {string} [props.featureName] - Shown in the optional badge area
 * @param {string} [props.badgeLabel]
 * @param {React.ReactNode} [props.illustration]
 * @param {{ label: string, to: string }|null} [props.primaryButton]
 * @param {{ label: string, to: string }|null} [props.secondaryButton]
 * @param {Array<{ label: string, to: string, icon?: React.ComponentType }>} [props.navLinks]
 * @param {boolean} [props.showNavLinks]
 */
export default function ComingSoon({
  title = "Something Exciting is Coming Soon",
  description = "We're working hard to launch this feature. Stay tuned for upcoming updates.",
  featureName,
  badgeLabel = "Under Development",
  illustration,
  primaryButton = null,
  secondaryButton = null,
  navLinks = null,
  showNavLinks = false,
}) {
  const showActions = Boolean(primaryButton?.to || secondaryButton?.to);

  return (
    <section className="coming-soon" aria-labelledby="coming-soon-title">
      <div className="coming-soon__panel">
        <div className="coming-soon__visual" aria-hidden={illustration ? undefined : true}>
          {illustration || <ComingSoonIllustration />}
        </div>

        <div className="coming-soon__content">
          {badgeLabel ? (
            <span className="coming-soon__badge">
              <span className="coming-soon__badge-dot" aria-hidden />
              {badgeLabel}
              {featureName ? <span className="coming-soon__badge-feature">· {featureName}</span> : null}
            </span>
          ) : null}

          <h1 id="coming-soon-title" className="coming-soon__title">
            {title}
          </h1>
          <p className="coming-soon__description">{description}</p>

          {showActions ? (
            <div className="coming-soon__actions">
              {primaryButton?.to ? (
                <Link to={primaryButton.to} className="coming-soon__btn coming-soon__btn--primary">
                  {primaryButton.label}
                </Link>
              ) : null}
              {secondaryButton?.to ? (
                <Link to={secondaryButton.to} className="coming-soon__btn coming-soon__btn--outline">
                  {secondaryButton.label}
                </Link>
              ) : null}
            </div>
          ) : null}

          {showNavLinks && navLinks?.length ? (
            <nav className="coming-soon__nav" aria-label="Helpful links">
              <p className="coming-soon__nav-label">Helpful links</p>
              <ul className="coming-soon__nav-list">
                {navLinks.map((item) => {
                  const Icon = item.icon;
                  return (
                    <li key={`${item.to}-${item.label}`}>
                      <Link to={item.to} className="coming-soon__nav-link">
                        {Icon ? <Icon aria-hidden /> : null}
                        <span>{item.label}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </nav>
          ) : null}
        </div>
      </div>
    </section>
  );
}
